// src/views/catalog/useCatalogTermEdit.ts
import {
  Dispatch, SetStateAction, useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import API, { CatalogEntry } from '../../services/ApiService';
import { fetchMe } from '../auth/slice/actions';
import { error, success } from '../../components/Message';
import { catalogErrorCode, catalogErrorKey, isCatalogTerm } from '../../utils/catalog';
import { matchesSearch, toSearchWords } from '../../utils/search';
import classes from './CatalogEntriesView.module.less';

// Poprawiany zwrot: który wiersz i co jest teraz w polu
type TermEdit = { conceptKey: string; draft: string };

// Przeniesienie obrazu pod poprawiony zwrot. Stary wpis znika dopiero wtedy, gdy obraz już jest pod
// nowym — przerwana poprawka zostawia go pod oboma zwrotami, ale go nie gubi. „Para już jest” znaczy,
// że obraz stoi już pod nowym zwrotem (choćby po przerwanej poprawce): zostaje usunąć stary wpis.
const moveEntry = async (entry: CatalogEntry, term: string): Promise<void> => {
  try {
    await API.catalog.add(term, entry.assetId);
  } catch (e) {
    if (catalogErrorCode(e) !== 'CATALOG_ENTRY_EXISTS') {
      throw e;
    }
  }

  await API.catalog.withdraw(entry.id);
};

interface UseCatalogTermEditParams {
  // Wszystkie wpisy kuratora, także usunięte
  entries: CatalogEntry[];
  loadEntries: () => Promise<void>;
  setFilter: Dispatch<SetStateAction<string>>;
}

// Poprawianie zwrotów na ekranie katalogu: pole w miejscu zwrotu, okno przed połączeniem z innym wierszem
// i podświetlenie wiersza po zapisie. Serwer nie zmienia zwrotu wpisu — poprawka to dodanie każdego obrazu
// pod nowym zwrotem i usunięcie starego wpisu, tymi samymi zapytaniami co dodawanie i krzyżyk.
export const useCatalogTermEdit = ({ entries, loadEntries, setFilter }: UseCatalogTermEditParams) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [termEdit, setTermEdit] = useState<TermEdit | null>(null);
  const [isSavingTerm, setIsSavingTerm] = useState(false);
  const termEditRef = useRef<HTMLInputElement>(null);
  // Ref obok stanu, bo blur pola przychodzi, zanim React pokaże nowy stan. W trakcie zapisu — także
  // gdy fokus zabiera okno potwierdzenia — blur nie zaczyna drugiego zapisu.
  const savingTermRef = useRef(false);
  // Wiersz po udanej poprawce: lista przewija się do niego, a on na chwilę się podświetla
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  const highlightedTermRef = useRef<HTMLSpanElement>(null);

  const setSavingTerm = useCallback((value: boolean) => {
    savingTermRef.current = value;
    setIsSavingTerm(value);
  }, []);

  const startTermEdit = useCallback((conceptKey: string) => {
    setTermEdit({ conceptKey, draft: conceptKey });
  }, []);

  const changeTermDraft = useCallback((draft: string) => {
    setTermEdit((current) => current && { ...current, draft });
  }, []);

  // Pole poprawki dostaje fokus z kursorem na końcu — zwykle poprawia się literówkę, a nie pisze od nowa
  const editedConceptKey = termEdit?.conceptKey;

  useEffect(() => {
    const field = termEditRef.current;

    if (editedConceptKey && field) {
      field.focus();
      field.setSelectionRange(field.value.length, field.value.length);
    }
  }, [editedConceptKey]);

  // Okno potwierdzenia jako obietnica: `true` — „Połącz”, `false` — „Anuluj” albo Esc. Fokus od razu na
  // „Anuluj”, żeby przytrzymany Enter z pola nie połączył wierszy. Zwrot idzie do tytułu bez zamiany na kody
  // HTML, bo „one's” wyszłoby jako „one&#39;s” — przed wstrzyknięciem i tak chroni React.
  const confirmMerge = useCallback((conceptKey: string): Promise<boolean> => new Promise((resolve) => {
    Modal.confirm({
      className: classes.mergeModal,
      title: t('words-list:catalog.entries.mergeTitle', { term: conceptKey, interpolation: { escapeValue: false } }),
      content: t('words-list:catalog.entries.mergeContent'),
      okText: t('words-list:catalog.entries.merge'),
      cancelText: t('words-list:catalog.entries.cancel'),
      autoFocusButton: 'cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  }), [t]);

  // Poprawka zwrotu całego wiersza. Jaki to zwrot, rozstrzyga serwer — dla niego „Give up.” i „give up”
  // to to samo — więc najpierw pytamy `lookup`. Z innym wierszem łączymy tylko po potwierdzeniu, bo
  // połączonych nie da się potem rozdzielić poprawką.
  const saveTermEdit = useCallback(async (conceptKey: string, draft: string) => {
    if (savingTermRef.current) {
      return;
    }

    // Wyczyszczone pole nie ma czego zapisać — zamyka się, a zwrot zostaje jak był
    if (draft === conceptKey || !isCatalogTerm(draft)) {
      setTermEdit(null);

      return;
    }

    setSavingTerm(true);

    try {
      const { data } = await API.catalog.lookup([draft]);
      const newConceptKey = data.results[0]?.conceptKey;

      // Same znaki bez liter i cyfr, np. „...” — dla serwera pusty zwrot
      if (!newConceptKey) {
        error(t('words-list:catalog.errors.noTerm'));

        return;
      }

      // Dla serwera to ten sam zwrot, np. po zmianie wielkości liter — nie ma czego przenosić
      if (newConceptKey === conceptKey) {
        setTermEdit(null);

        return;
      }

      const isMerge = entries.some((entry) => entry.active && entry.conceptKey === newConceptKey);

      // „Anuluj” porzuca poprawkę. Pole zostawione otwarte po zamknięciu okna dostałoby z powrotem fokus
      // i przy pierwszym kliknięciu obok zapytało o połączenie jeszcze raz.
      if (isMerge && !(await confirmMerge(newConceptKey))) {
        setTermEdit(null);

        return;
      }

      // Obrazy wiersza jak na ekranie: aktywne wpisy tego zwrotu, w kolejności dodania. Przenosimy je po
      // kolei, nie naraz: kolejność dodania to kolejność obrazów zwrotu, a pierwszy bierze „Uzupełnij”.
      await entries
        .filter((entry) => entry.active && entry.conceptKey === conceptKey)
        .reduce<Promise<void>>((previous, entry) => previous.then(() => moveEntry(entry, draft)), Promise.resolve());

      // Wiersz przeskoczył w alfabecie. Wyszukiwarka zostaje, jeśli dalej do niego pasuje — przy kilku
      // poprawkach pod jednym filtrem nie trzeba go wpisywać od nowa; przy literówce („giev”) się czyści.
      await loadEntries();
      setFilter((current) => (matchesSearch(newConceptKey, toSearchWords(current)) ? current : ''));
      setTermEdit(null);
      setHighlightedKey(newConceptKey);
      success(t('words-list:catalog.entries.editSuccess'));
    } catch (e) {
      // Pole zostaje otwarte z poprawką — ponowny zapis dokończy przenoszenie
      error(t(catalogErrorKey(e)));

      if (catalogErrorCode(e) === 'CATALOG_NOT_CURATOR') {
        dispatch(fetchMe());
      }

      // Część obrazów mogła już przejść pod nowy zwrot — lista ma pokazać, co naprawdę jest w katalogu
      loadEntries();
    } finally {
      setSavingTerm(false);
    }
  }, [confirmMerge, dispatch, entries, loadEntries, setFilter, setSavingTerm, t]);

  // Przewinięcie na środek ekranu i poświata, która gaśnie po 2 s. Potem zdejmujemy podświetlenie, żeby
  // następna poprawka tego samego wiersza zapaliła je od nowa.
  useEffect(() => {
    if (!highlightedKey) {
      return undefined;
    }

    highlightedTermRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const timer = setTimeout(() => setHighlightedKey(null), 2000);

    return () => clearTimeout(timer);
  }, [highlightedKey]);

  return {
    termEdit,
    isSavingTerm,
    termEditRef,
    highlightedKey,
    highlightedTermRef,
    startTermEdit,
    changeTermDraft,
    saveTermEdit,
  };
};

export default useCatalogTermEdit;
